class SiteCart extends HTMLElement {
  constructor() {
    super();
    this.prevFocusedElement = null;

    this.els = {
      body: document.body,
      subtotal: document.querySelector(".cart-subtotal"),
      checkout: document.querySelector(".cart-checkout"),
      footer: document.querySelector(".cart-footer"),
      items: document.querySelector(".cart-items"),
      empty: document.querySelector(".cart-empty"),
      count: document.querySelector(".cart-count"),
      fill: document.querySelector(".cart-fill"),
      toggle: document.querySelector(".cart-toggle"),
      cartContent: document.querySelector(".cart-content"),
      cartContentContainer: document.querySelector(".cart-content-container"),
    };

    this.handleFocusTrap = this.handleFocusTrap.bind(this);
    this.onEsc = this.onEsc.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    document.addEventListener("keyup", this.onEsc);
    window.addEventListener("resize", this.onResize);
  }

  disconnectedCallback() {
    document.removeEventListener("keyup", this.onEsc);
    window.removeEventListener("resize", this.onResize);
  }

  openCart() {
    const siteHeader = document.querySelector(".site-header");
    const cartContentContainer = this.els.cartContentContainer;

    this.prevFocusedElement = document.activeElement;

    document.body.classList.add("overflow-hidden", "cart--open");
    if (siteHeader) siteHeader.classList.add("toggle--open", "cart--open");

    if (cartContentContainer) {
      cartContentContainer.classList.remove("invisible", "opacity-0");
      cartContentContainer.classList.add("visible", "opacity-100");
      cartContentContainer.setAttribute("tabindex", "-1");
      cartContentContainer.focus();
    }

    document.dispatchEvent(new CustomEvent("cartOpened"));
    document.addEventListener("keydown", this.handleFocusTrap);
  }

  closeCart() {
    const siteHeader = document.querySelector(".site-header");
    const cartContentContainer = this.els.cartContentContainer;

    document.body.classList.remove("overflow-hidden", "cart--open");
    if (siteHeader) siteHeader.classList.remove("toggle--open", "cart--open");

    if (cartContentContainer) {
      cartContentContainer.classList.remove("visible", "opacity-100");
      cartContentContainer.classList.add("invisible", "opacity-0");
    }

    setTimeout(() => {
      if (
        this.prevFocusedElement &&
        typeof this.prevFocusedElement.focus === "function"
      ) {
        this.prevFocusedElement.focus();
      } else if (this.els.toggle) {
        this.els.toggle.focus?.();
      }
    }, 300);

    document.removeEventListener("keydown", this.handleFocusTrap);
  }

  buildConfig(body) {
    return {
      body,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };
  }

  add(id, quantity = 1) {
    this.openCart();

    const items = [{ id, quantity }];
    const body = JSON.stringify({ items });
    const config = this.buildConfig(body);

    return fetch(`/cart/add`, config)
      .then((r) => r.json())
      .then(async () => {
        await this.updateCart();
      })
      .catch(() => {});
  }

  getCart() {
    return fetch("/cart.js").then((r) => r.json());
  }

  async updateCart() {
    this.disableCheckout();

    const cart = await this.getCart(); // { items, total_price, ... }
    const count = this.calculateCartCount(cart?.items || []);

    this.updateDisplayUI(count);
    this.updateItemsUI(cart.items || []);
    this.updateSubtotalUI(cart.total_price || 0);

    this.enableCheckout();
  }

  renderCartItem(item, isLast) {
    const {
      url,
      image,
      quantity,
      price,
      variant_id: variantId,
      product_title: productTitle,
      options_with_values: optionsWithValues = [],
    } = item;

    const formattedPrice = this.priceToCurrency(price);
    const nonLastClasses = !isLast ? `border-b border-gray-300` : ``;

    const productLabel = this.els.items?.dataset?.productLabel || "";
    const priceLabel = this.els.items?.dataset?.priceLabel || "";
    const removeLabel = this.els.items?.dataset?.removeLabel || "Remove";

    return `
      <div class="cart-item grid items-start py-4 ${nonLastClasses}">
        <div class="cart-item-id visually-hidden" data-variant-id="${variantId}"></div>
        ${this.renderImage(image, productTitle)}
        ${this.renderProductTitle(productLabel, productTitle, url)}
        ${this.renderVariants(optionsWithValues)}
        ${this.renderQuantity(quantity, variantId)}
        ${this.renderRemove(variantId, removeLabel, productTitle)}
        ${this.renderPrice(formattedPrice, priceLabel)}
      </div>
    `;
  }

  renderImage(image, title) {
    return `
      <div class="cart-item-image border border-gray-300 bg-gray-100 w-20 h-28 lg:w-32 lg:h-40 relative mb-3 lg:mb-0">
        <img class="absolute w-full h-full inset-0 object-cover object-center" src="${image}" alt="${this.escape(
      title
    )}" loading="lazy" />
      </div>
    `;
  }

  renderProductTitle(label, title, url) {
    return `
      <div class="cart-item-title mb-2 md:mb-3 lg:mb-0">
        <p><a href="${url}">${this.escape(title)}</a></p>
      </div>
    `;
  }

  renderVariants(optionsWithValues) {
    const html = (optionsWithValues || [])
      .map((o) =>
        o.value !== "Default Title"
          ? `<div class="mb-2 md:mb-3 lg:mb-6"><p>${this.escape(
              o.value
            )}</p></div>`
          : ``
      )
      .join("");
    return `<div class="cart-item-variants">${html}</div>`;
  }

  renderPrice(price, label) {
    return `
      <div class="cart-item-price lg:flex lg:justify-end lg:pt-2 lg:pr-4">
        <p>${price}</p>
      </div>
    `;
  }

  renderQuantity(quantity, id) {
    return `
      <div class="cart-item-quantity w-20">
        <div class="w-full inline-block border border-gray-300 h-10 relative">
          <div class="w-full flex justify-between items-center px-3 h-full">
            <span class="caption text-xs">${quantity}</span>
            <div class="transform-gpu rotate-90">
              <svg width="8" viewBox="0 0 12 22" xmlns="http://www.w3.org/2000/svg">
                <g fill="none" stroke-linejoin="round">
                  <path d="M1.7 1.6L10.6 10.6C10.7 10.7 10.8 10.8 10.8 11 10.8 11.2 10.7 11.3 10.6 11.4L1.7 20.4" stroke-width="1.6" stroke="currentColor"></path>
                </g>
              </svg>
            </div>
          </div>
          <select
            name="quantity"
            data-id="${id}"
            class="opacity-0 absolute top-0 left-0 w-full h-full"
            tabindex="0"
            aria-label="Select quantity"
          >
            ${[1, 2, 3, 4, 5]
              .map(
                (n) =>
                  `<option value="${n}" ${
                    quantity === n ? "selected" : ""
                  }>${n}</option>`
              )
              .join("")}
          </select>
        </div>
      </div>
    `;
  }

  renderRemove(id, label, title) {
    return `
      <div class="grid justify-end lg:justify-start">
        <button data-id="${id}" class="cart-item-remove group grid grid-flow-col gap-3 items-center" aria-label="Remove ${this.escape(
      title
    )}" tabindex="0">
          <span class="caption text-gray-500 order-1 lg:order-2">${this.escape(
            label
          )}</span>
          <span class="grid w-10 h-10 border transition-border duration-200 border-gray-300 rounded-full items-center justify-center group-hover:border-black order-2 lg:order-1">
            <svg width="10" height="10" xmlns="http://www.w3.org/2000/svg">
              <g stroke="#000" fill="none" fill-rule="evenodd"><path d="m.5.5 9 9M9.5.5l-9 9"/></g>
            </svg>
          </span>
        </button>
      </div>
    `;
  }

  updateSubtotalUI(totalCents) {
    if (!this.els.subtotal) return;
    this.els.subtotal.textContent = this.priceToCurrency(totalCents);
  }

  updateItemsUI(items) {
    if (!this.els.items) return;
    const html = (items || [])
      .map((item, i) => this.renderCartItem(item, i === items.length - 1))
      .join("\n");
    this.els.items.innerHTML = html;
  }

  updateDisplayUI(count) {
    if (this.els.toggle) this.els.toggle.textContent = `Cart (${count})`;

    if (count === 0) {
      this.hide(this.els.footer);
      this.show(this.els.empty);
      if (this.els.fill) this.els.fill.style.fill = "none";
      this.hide(this.els.count);
    } else {
      this.show(this.els.footer);
      this.hide(this.els.empty);
      if (this.els.fill) this.els.fill.style.fill = "black";
      if (this.els.count) {
        this.show(this.els.count);
        this.els.count.textContent = String(count);
      }
    }
  }

  disableCheckout() {
    if (this.els.checkout) this.els.checkout.setAttribute("href", "");
  }

  enableCheckout() {
    if (this.els.checkout) this.els.checkout.setAttribute("href", "/checkout");
  }

  calculateCartCount(items) {
    if (!Array.isArray(items)) return 0;
    return items.reduce((acc, { quantity }) => acc + Number(quantity || 0), 0);
  }

  // ---------- Utilities ----------
  handleFocusTrap(e) {
    if (!this.els.cartContentContainer) return;

    const focusable =
      'button, [href], a, input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const elementsToFocus =
      this.els.cartContentContainer.querySelectorAll(focusable);

    const visible = Array.from(elementsToFocus).filter((el) => {
      const styles = window.getComputedStyle(el);
      return styles.display !== "none" && styles.visibility !== "hidden";
    });

    if (!visible.length) return;

    const first = visible[0];
    const last = visible[visible.length - 1];

    if (e.key === "Tab" || e.keyCode === 9) {
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  }

  onEsc(e) {
    if (e.key === "Escape") this.closeCart();
  }

  onResize() {
    const topTier = document.querySelector(".site-header__top-tier");
    const bottomTier = document.querySelector(".site-header__bottom-tier");
    const w = `${document.body.clientWidth}px`;
    if (topTier) topTier.style.width = w;
    if (bottomTier) bottomTier.style.width = w;
  }

  priceToCurrency(cents) {
    const val = Number(cents || 0) / 100;
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(val);
  }

  numberWithCommas(x) {
    return String(x).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  show(el) {
    if (!el) return;
    el.style.display = "";
    el.classList.remove("hidden");
  }

  hide(el) {
    if (!el) return;
    el.style.display = "none";
  }

  escape(s) {
    return String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}

customElements.define("site-cart", SiteCart);

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const serializeForm = (form) => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    obj[key] = formData.get(key);
  }
  return JSON.stringify(obj);
};

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector("ul");
    this.sliderItems = this.querySelectorAll("li");
    this.pageCount = this.querySelector(".slider-counter--current");
    this.pageTotal = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    if (!this.sliderItems.length === 0) return;
    this.slidesPerPage = Math.floor(
      this.slider.clientWidth / this.sliderItems[0].clientWidth
    );
    this.totalPages = this.sliderItems.length - this.slidesPerPage + 1;
    this.update();
  }

  update() {
    if (!this.pageCount || !this.pageTotal) return;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItems[0].clientWidth) + 1;

    if (this.currentPage === 1) {
      this.prevButton.setAttribute("disabled", true);
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (this.currentPage === this.totalPages) {
      this.nextButton.setAttribute("disabled", true);
    } else {
      this.nextButton.removeAttribute("disabled");
    }

    this.pageCount.textContent = this.currentPage;
    this.pageTotal.textContent = this.totalPages;
  }

  onButtonClick(event) {
    event.preventDefault();
    const slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + this.sliderItems[0].clientWidth
        : this.slider.scrollLeft - this.sliderItems[0].clientWidth;
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class VideoComponent extends HTMLElement {
  constructor() {
    super();
    this.initObserver();
  }
  initObserver() {
    let loaded = false;
    const video = this.querySelector("video");
    const observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          if (loaded) {
            video.play();
          } else {
            if (video.getAttribute("autoplay") == "autoplay") {
              Array.from(video.querySelectorAll("source")).forEach((source) => {
                source.src = source.dataset.src;
              });
            }
            video.load();
            video.play();
            loaded = true;
          }
        } else {
          if (loaded) {
            video.pause();
          }
        }
      });
    });
    observer.observe(video);
  }
}

customElements.define("video-component", VideoComponent);

// todo: move vimeo pause to component that extends teddy-modal

class TeddyModal extends HTMLElement {
  constructor() {
    super();
    this.vimeoPlayer = null;
    document.addEventListener("DOMContentLoaded", () => {
      this.openSelectors = this.dataset.openSelectors || "";
      this.isOpen = this.dataset.isOpen;
      this.initClickHandlers();
      if (this.isOpen) this.openModal();
    });
  }

  initClickHandlers() {
    const content = this.querySelector(".modal__content");

    this.addEventListener("click", (e) => {
      const closeBtn = e.target.closest("button.modal__close");
      const isOutside = content ? !content.contains(e.target) : false;
      if (closeBtn || isOutside) this.closeModal();
    });

    document.addEventListener("click", (e) => {
      const target = e.target;
      const isOpen = this.openSelectors
        .split(",")
        .some((s) => s && (target.matches(s) || target.closest(s)));
      if (isOpen) {
        e.preventDefault();
        this.openModal();
      }
    });
  }

  ensureVimeoPlayer() {
    const iframe = this.querySelector('iframe[src*="player.vimeo.com"]');
    if (!iframe || !window.Vimeo) return null;
    if (!this.vimeoPlayer || this.vimeoPlayer.element !== iframe) {
      this.vimeoPlayer = new window.Vimeo.Player(iframe);
    }
    return this.vimeoPlayer;
  }

  closeModal() {
    const modal = this.querySelector(".modal");
    const player = this.ensureVimeoPlayer();
    if (player) {
      player.pause().catch(() => {});
      player.setCurrentTime(0).catch(() => {});
    }
    if (modal) modal.classList.remove("active");
  }

  openModal() {
    const modal = this.querySelector(".modal");
    if (modal) modal.classList.add("active");
  }
}

customElements.define("teddy-modal", TeddyModal);

class SignupModal extends HTMLElement {
  constructor() {
    super();
    this.checkRequired = false;
    this.warning = null;
    this.emailSource = this.dataset.emailSource;
    this.smsSource = this.dataset.smsSource;
  }

  connectedCallback() {
    this.initSubmitHandlers();
    this.checkRequired = this.dataset.checkRequired === "true";

    if (this.checkRequired) {
      this.warning = this.querySelector(".js-signup-modal-warning");
      this.attachCheckboxListeners();
    }
  }

  attachCheckboxListeners() {
    const checkboxes = this.querySelectorAll(".js-signup-modal-checkbox");
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        const anyChecked = Array.from(checkboxes).some((cb) => cb.checked);
        if (anyChecked && this.warning) {
          this.warning.classList.add("visually-hidden");
          this.warning.setAttribute("aria-hidden", "true");
        }
      });
    });
  }

  initSubmitHandlers() {
    this.addEventListener("submit", async (e) => {
      if (e.target.classList.contains("newsletter-signup-modal__form")) {
        e.preventDefault();

        const form = e.target;
        const companyId = "JFDzfK";
        const button = form.querySelector('button[type="submit"]');

        if (this.checkRequired) {
          const checkedInterests = form.querySelectorAll(
            ".js-signup-modal-checkbox:checked"
          );

          if (checkedInterests.length === 0) {
            this.warning.classList.remove("visually-hidden");
            this.warning.setAttribute("aria-hidden", "false");
            return;
          }
        }

        const data = new FormData(form);
        const formSource = data.get("custom_source");
        const firstName = data.get("first_name");
        const email = data.get("email");
        const phone = data.get("phone");

        const listId = form.dataset.listId;
        if (formSource) this.source = formSource;
        const interests = Array.from(
          form.querySelectorAll('input[name="content-interests"]:checked')
        ).map((checkbox) => checkbox.id);

        const inputTelIntl = this.querySelector("input-tel-intl");
        const fmtPhone = inputTelIntl.format(phone);

        button.innerText = "Subscribing...";

        const payload = {
          data: {
            type: "subscription",
            attributes: {
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email,
                    phone_number: fmtPhone,
                    first_name: firstName,
                    properties: {
                      "Email Content Interests": interests,
                      ...(this.emailSource
                        ? {
                            "signup source - email": this.emailSource,
                          }
                        : {}),
                      ...(this.smsSource
                        ? {
                            "signup source - sms": this.smsSource,
                          }
                        : {}),
                    },
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: "SUBSCRIBED",
                        },
                      },
                      sms: {
                        marketing: {
                          consent: "SUBSCRIBED",
                        },
                        transactional: {
                          consent: "SUBSCRIBED",
                        },
                      },
                    },
                  },
                },
              },
            },
            ...(listId && {
              relationships: {
                list: {
                  data: {
                    type: "list",
                    id: listId,
                  },
                },
              },
            }),
          },
        };

        try {
          try {
            await fetch(
              "https://teddy-baldassarre.vercel.app/api/customer-with-tags",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  input: { firstName, email, phone: fmtPhone },
                  tags: ["Single-Step Signup Completed"],
                }),
              }
            ).then((r) => r.json());
          } catch (err) {
            console.error("Customer tag update failed", err);
          }

          const response = await fetch(
            `https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`,
            {
              method: "POST",
              headers: {
                Accept: "application/vnd.api+json",
                "Content-Type": "application/vnd.api+json",
                revision: "2025-01-15",
              },
              body: JSON.stringify(payload),
            }
          );

          if (response.ok) {
            localStorage.setItem("teddy-signup", "true");
            form.closest(".modal").classList.add("success");
          } else {
            this.warning.classList.remove("visually-hidden");
            this.warning.setAttribute("aria-hidden", "false");
            this.warning.innerText = "Invalid phone number.";

            button.innerText = "Subscribe";
          }
        } catch (err) {
          console.error("Error during subscription:", err);
          button.innerText = "Subscribe";
        }
      }
    });
  }
}

customElements.define("signup-modal", SignupModal);

class SignupEmail extends HTMLElement {
  constructor() {
    super();
    this.initSubmitHandler();
    this.emailSource = this.dataset.emailSource;
    this.smsSource = this.dataset.smsSource;
  }

  initSubmitHandler() {
    this.addEventListener("submit", (e) => {
      if (e.target.classList.contains("newsletter-signup-email")) {
        e.preventDefault();
        Array.from(document.querySelectorAll(".modal")).forEach((modal) => {
          modal.classList.remove("active");
        });

        const data = new FormData(e.target);
        const email = data.get("email");
        const phone = data.get("phone");
        const $modal = document.querySelector(".newsletter-signup-modal");

        if ($modal) {
          const emailInput = $modal.querySelector('[name="email"]');
          const phoneInput = $modal.querySelector('[name="phone"]');
          const signupModal = $modal.querySelector("signup-modal");

          if (email && emailInput) {
            emailInput.value = email;
          }

          if (phone && phoneInput) {
            const inputIntl = this.querySelector("input-tel-intl");
            const iti = inputIntl.iti;
            const signupIntl = $modal.querySelector("input-tel-intl");

            signupIntl.setActiveCountry(iti.s.iso2);
            phoneInput.value = phone;
          }

          if (this.emailSource) {
            signupModal.emailSource = this.emailSource;
          }

          if (this.smsSource) {
            signupModal.smsSource = this.smsSource;
          }

          $modal.classList.add("active");
        }
      }
    });
  }
}

customElements.define("signup-email", SignupEmail);

class AuthorizedText extends HTMLElement {
  constructor() {
    super();
    document.addEventListener("DOMContentLoaded", () => {
      this.initAvailability();
    });
  }

  initAvailability() {
    const date = new Date();
    const est = new Date(
      date.toLocaleString("en-US", { timeZone: "America/New_York" })
    );
    const hour = est.getHours();
    const day = est.getDay();
    const avail = this.querySelector(".authorized-text__available");
    if (hour > 9 && hour < 17 && day !== 0 && day !== 6) {
      avail.style.background = "#60DE81";
    } else {
      avail.style.background = "transparent";
    }
  }
}

customElements.define("authorized-text", AuthorizedText);

class IntlURLConverter extends HTMLElement {
  connectedCallback() {
    const ISO_STORES = [
      "en-gb",
      "en-ca",
      "en-au",
      "en-de",
      "en-sg",
      "en-int",
      "en-in",
      "en-ch",
    ];

    const findSubstrings = (arr, target) => {
      let result = [];

      for (let i = 0; i < arr.length; i++) {
        if (target.includes(arr[i])) {
          result.push(arr[i]);
        }
      }

      return result;
    };

    const store = findSubstrings(ISO_STORES, window.location.href)[0];

    if (store) {
      const anchors = Array.from(this.querySelectorAll("a"));

      anchors.forEach((anchor) => {
        if (
          anchor.href.includes("teddy-baldassarre-watches.myshopify.com") ||
          anchor.href.includes("teddybaldassarre.com") ||
          (anchor.href[0] === "/" && !anchor.href.includes(store))
        ) {
          const url = new URL(anchor.href);
          const path = url.pathname;
          anchor.href = `/${store}${path}`;
        }
      });
    }
  }
}

customElements.define("intl-url-converter", IntlURLConverter);

class IntlArticleURLConverter extends HTMLElement {
  connectedCallback() {
    const ISO_STORES = [
      "en-gb",
      "en-ca",
      "en-au",
      "en-de",
      "en-sg",
      "en-int",
      "en-in",
      "en-ch",
    ];

    const findSubstrings = (arr, target) => {
      let result = [];

      for (let i = 0; i < arr.length; i++) {
        if (target.includes(arr[i])) {
          result.push(arr[i]);
        }
      }

      return result;
    };

    const store = findSubstrings(ISO_STORES, window.location.href)[0];

    const anchors = Array.from(this.querySelectorAll("a"));

    anchors.forEach((anchor) => {
      const isInTeddyLink =
        anchor.href.includes("teddy-baldassarre-watches.myshopify.com") ||
        anchor.href.includes("teddybaldassarre.com");

      const shouldAddStore =
        store &&
        (isInTeddyLink ||
          (anchor.href[0] === "/" && !anchor.href.includes(store)));

      if (shouldAddStore) {
        const url = new URL(anchor.href);
        const path = url.pathname;
        anchor.href = `/${store}${path}${articleUtm}`;
      }

      if (isInTeddyLink) {
        const articleHandle =
          articleContent.dataset.articleHandle.split("/")[1];
        const articleUtm = `?utm_term=In_Article_link&utm_content=Blog_Article_${articleHandle}`;
        anchor.href = anchor.href + articleUtm;
      }
    });
  }
}

customElements.define("intl-article-url-converter", IntlArticleURLConverter);

class SiteCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    const rawConfig = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 1, spaceBetween: 20, loop: true };

    const config = { ...rawConfig };

    this.swiper = new Swiper(`#${this.dataset.id} .swiper`, {
      ...config,
      slideToClickedSlide: false,
      preventClicks: true,
      preventClicksPropagation: true,
      pagination: {
        el: `#${this.dataset.id} .swiper-pagination`,
        clickable: true,
        bulletActiveClass: "active",
      },
      navigation: {
        nextEl: `#${this.dataset.id} .site-carousel-next`,
        prevEl: `#${this.dataset.id} .site-carousel-prev`,
      },
    });

    this.initThumbnails();
  }

  initThumbnails() {
    const thumbs = Array.from(this.querySelectorAll(".site-carousel-thumb"));

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener("click", () => {
        this.slideTo(index);
      });
    });
  }

  prev() {
    if (this.swiper) this.swiper.slidePrev();
  }

  next() {
    if (this.swiper) this.swiper.slideNext();
  }

  slideTo(i) {
    if (!this.swiper) return;
    this.swiper.slideTo(i);
  }
}

if (!customElements.get("site-carousel")) {
  customElements.define("site-carousel", SiteCarousel);
}

class YtVideoDuration extends HTMLElement {
  static get observedAttributes() {
    return ["video-id", "format"];
  }

  static cache = new Map();
  static apiReady = null;
  static playerReady = null;
  static player = null;
  static queue = [];
  static processing = false;

  connectedCallback() {
    this.span =
      this.querySelector("span") ||
      this.appendChild(document.createElement("span"));
    this.vid = this.getAttribute("video-id") || this.getAttribute("id") || "";
    this.format = this.getAttribute("format") || "auto";
    if (!this.vid) return;
    if (!this._io) {
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            this._io.disconnect();
            this.load();
          }
        });
      });
      this._io.observe(this);
    }
  }

  attributeChangedCallback() {
    if (this.isConnected) this.connectedCallback();
  }

  async load() {
    const secs = await YtVideoDuration.getDuration(this.vid);
    this.span.textContent = secs ? YtVideoDuration.fmt(secs, this.format) : "";
    this.span.classList.remove("hidden");
  }

  static loadIframeAPI() {
    if (window.YT && window.YT.Player) return Promise.resolve();
    if (this.apiReady) return this.apiReady;
    this.apiReady = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        try {
          if (typeof prev === "function") prev();
        } catch {}
        resolve();
      };
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(s);
    });
    return this.apiReady;
  }

  static async ensurePlayer() {
    if (this.player) return this.playerReady;
    await this.loadIframeAPI();
    this.playerReady = new Promise((resolve) => {
      const box = document.createElement("div");
      box.style.position = "fixed";
      box.style.left = "-99999px";
      box.style.top = "0";
      box.style.width = "1px";
      box.style.height = "1px";
      document.body.appendChild(box);
      this.player = new YT.Player(box, {
        videoId: "dQw4w9WgXcQ",
        host: "https://www.youtube-nocookie.com",
        playerVars: { controls: 0, disablekb: 1, modestbranding: 1, rel: 0 },
        events: { onReady: () => resolve(), onError: () => resolve() },
      });
    });
    return this.playerReady;
  }

  static getDuration(videoId) {
    if (this.cache.has(videoId)) return this.cache.get(videoId);
    const p = new Promise(async (resolve) => {
      this.queue.push({ videoId, resolve });
      if (!this.processing) this.processQueue();
    });
    this.cache.set(videoId, p);
    return p;
  }

  static async processQueue() {
    this.processing = true;
    await this.ensurePlayer();
    while (this.queue.length) {
      const { videoId, resolve } = this.queue.shift();
      const secs = await this.cueAndReadDuration(videoId).catch(() => null);
      resolve(secs);
    }
    this.processing = false;
  }

  static cueAndReadDuration(videoId) {
    return new Promise((resolve) => {
      const done = (v) =>
        resolve(Number.isFinite(v) && v > 0 ? Math.round(v) : null);
      const handler = (e) => {
        const st = window.YT && YT.PlayerState;
        if (!st) {
          done(null);
          return;
        }
        if (
          e.data === st.CUED ||
          e.data === st.PAUSED ||
          e.data === st.UNSTARTED
        ) {
          setTimeout(() => {
            try {
              done(this.player.getDuration());
            } catch {
              done(null);
            }
            try {
              this.player.removeEventListener("onStateChange", handler);
            } catch {}
          }, 0);
        }
      };
      try {
        this.player.addEventListener("onStateChange", handler);
        if (this.player.cueVideoById) this.player.cueVideoById(videoId);
        else done(null);
        setTimeout(() => {
          try {
            this.player.removeEventListener("onStateChange", handler);
          } catch {}
          try {
            done(this.player.getDuration());
          } catch {
            done(null);
          }
        }, 2500);
      } catch {
        done(null);
      }
    });
  }

  static fmt(total, mode) {
    total = Math.max(0, Math.floor(total || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0 || mode === "h:mm:ss")
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  }
}

customElements.define("yt-video-duration", YtVideoDuration);

class AddToCart extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.variantId = this.getAttribute("variant-id");
    this.quantity = parseInt(this.getAttribute("quantity") || "1", 10);

    this.button =
      this.querySelector("[data-add-to-cart]") ||
      this.querySelector("button") ||
      this.firstElementChild;

    if (!this.button) return;

    this.button.addEventListener("click", this.handleClick);
  }

  async handleClick(event) {
    event.preventDefault();

    const variantId =
      this.getAttribute("variant-id") ||
      this.button.getAttribute("data-variant-id");

    if (!variantId) return;

    const btn = this.button;
    const originalText = btn.textContent;
    const originalDisabled = btn.disabled;

    try {
      btn.disabled = true;
      btn.textContent = "Adding...";

      await this.addVariantToCart(variantId, this.quantity);

      btn.textContent = originalText;

      this.openCartOverlay();
    } catch (err) {
      console.error(err);
      btn.textContent = "Error";
      setTimeout(() => {
        btn.textContent = originalText;
      }, 1200);
    } finally {
      btn.disabled = originalDisabled;
    }
  }

  async addVariantToCart(variantId, quantity) {
    const numericId = Number(variantId);
    const qty = Number(quantity) || 1;

    const siteCart = document.querySelector("site-cart");
    if (siteCart && typeof siteCart.add === "function") {
      return siteCart.add(numericId, qty);
    }

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: numericId,
        quantity: qty,
      }),
    });

    if (!res.ok) {
      throw new Error("Failed to add to cart");
    }

    return res.json();
  }

  openCartOverlay() {
    const header = document.querySelector("site-header");
    if (!header) {
      return;
    }

    if (typeof header.openCartOverlayFromEvent === "function") {
      header.openCartOverlayFromEvent();
      return;
    }

    if (typeof header.toggleCartOverlay === "function") {
      if (header.activeOverlayType !== "cart") {
        header.toggleCartOverlay();
      }
      return;
    }

    const cartPanel = document.querySelector("[data-cart-panel]");

    if (cartPanel) {
      cartPanel.style.transition = "opacity 0.25s ease";
      cartPanel.style.opacity = "1";
      cartPanel.style.visibility = "visible";
      cartPanel.style.pointerEvents = "auto";
    }
  }
}

if (!customElements.get("add-to-cart")) {
  customElements.define("add-to-cart", AddToCart);
}
